import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import { GetHeatMapData } from "../../api/heatmap";
import { Block, Icon } from "../../components/Component";
import Content from "../../layout/content/Content";
import CustomDatePicker from "./CustomDatePicker";
import Map from "./Map";
import UsStateSelectModal from "./UsStateSelectModal";
import { useCookies } from "react-cookie";
import { useUserInfo } from "../../layout/provider/Theme";
import { formatCurrency } from "../../utils";

const HeatMap = () => {
  const [selectedZone, setSelectedZone] = useState("All Zones");
  const { settingsData } = useUserInfo();
  const [modal, setModal] = useState(false);
  const [mutation, setMutation] = useState({});
  const [stateReport, setStateReport] = useState(null);
  const [stateArray, setStateArray] = useState([]);
  const [filterData, setFilterData] = useState("this_month");
  const [stateData, setStateData] = useState({});
  const [stateFilterData, setStateFilterData] = useState([]);
  const [mapLoading, setMapLoading] = useState(true);
  const [nationalData, setNationalData] = useState({
    prepayValues: {
      avgSaving: 0,
      avgPrice: 0,
    },
    creditValues: {
      avgSaving: 0,
      avgPrice: 0,
    },
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [localMutation, setLocalMutation] = useState({});
  const [selectedColor, setSelectedColor] = useState([
    "#FFFF00",
    "#008000",
    "#FF0000",
  ]);
  const stateAverageSectionRef = useRef(null);
  const [cookies, setCookie] = useCookies(["pricingFilePetro"]);
  const savedPetro = cookies.pricingFilePetro;
  const [selectedNetwork, setSelectedNetwork] = useState(
    savedPetro ?? "loves"
  );

  const nationalHeatMapData = {
    prepayValues: {
      avgSaving: 0,
      avgPrice: 0,
    },
    creditValues: {
      avgSaving: 0,
      avgPrice: 0,
    },
  };
  const handleColorChange = (color) => {
    setStateReport(null);
    setSelectedColor((prevSelectedColors) => {
      if (prevSelectedColors.includes(color)) {
        // Remove color if already selected
        return prevSelectedColors.filter((item) => item !== color);
      } else {
        // Add color if not selected
        return [...prevSelectedColors, color];
      }
    });
  };

  useEffect(() => {
    setCookie("pricingFilePetro", selectedNetwork, {
      path: "/",
      maxAge: 60 * 60 * 24 * 30,
      sameSite: "lax",
    });
  }, [selectedNetwork, setCookie]);

  const filterStatesByColor = (data, colors) => {
    return Object.fromEntries(
      Object.entries(data).filter(([state, details]) =>
        colors.includes(details.color)
      )
    );
  };

  useEffect(() => {
    const filteredStatesByColors = filterStatesByColor(
      stateData,
      selectedColor
    );
    setStateFilterData(filteredStatesByColors);
    if (Object.keys(stateData)?.length) {
      setLocalMutation({});
    }
    calculateNationalAverage(filteredStatesByColors);
  }, [selectedColor, mutation, stateData]);

  const handleSelectedZone = (value) => setSelectedZone(value);
  const handleNavigate = () => {
    if (stateAverageSectionRef.current) {
      stateAverageSectionRef.current.scrollIntoView({
        behavior: "smooth",
      });
    }
  };
  useEffect(() => {
    if (stateReport) {
      handleNavigate();
    }
  }, [stateReport]);

  const handleHeatMap = async () => {
    setMapLoading(true);
    try {
      const response = await GetHeatMapData({
        dateFilter: filterData,
        statesCodes: stateArray,
        network: selectedNetwork,
      });
      if (response?.data?.data?.isNoDataFound) {
        setErrorMessage(response?.data?.message);
      } else {
        setErrorMessage("");
      }
      setStateData(response?.data?.data?.getAllStatesHeatMapData ?? {});
      setStateFilterData(
        filterStatesByColor(
          response?.data?.data?.getAllStatesHeatMapData ?? {},
          selectedColor
        )
      );

      calculateNationalAverage(
        response?.data?.data?.getAllStatesHeatMapData ?? {}
      );

      setStateReport(null);
    } catch (error) {
      console.error(error);
      setStateData({});
      // toast.error(error?.response?.data?.message, {
      //   position: "top-center",
      // });
      setErrorMessage(error?.response?.data?.message || "");
      setNationalData({
        prepayValues: {
          avgSaving: 0,
          avgPrice: 0,
        },
        creditValues: {
          avgSaving: 0,
          avgPrice: 0,
        },
      });
    } finally {
      setMapLoading(false);
    }
  };

  const calculateNationalAverage = (getAllStatesHeatMapData) => {
    const totalStatesLengthForNationalCalc = Object.keys(
      getAllStatesHeatMapData
    )?.length;

    for (const key in getAllStatesHeatMapData) {
      if (key?.trim()) {
        const stateDataElement = getAllStatesHeatMapData[key];
        nationalHeatMapData.creditValues.avgSaving +=
          stateDataElement.creditValues.avgSaving;
        nationalHeatMapData.creditValues.avgPrice +=
          stateDataElement.creditValues.avgPrice;
        nationalHeatMapData.prepayValues.avgSaving +=
          stateDataElement.prepayValues.avgSaving;
        nationalHeatMapData.prepayValues.avgPrice +=
          stateDataElement.prepayValues.avgPrice;
      }
    }
    if (totalStatesLengthForNationalCalc === 0) {
      nationalHeatMapData.prepayValues.avgPrice = 0;
      nationalHeatMapData.prepayValues.avgSaving = 0;
      nationalHeatMapData.creditValues.avgPrice = 0;
      nationalHeatMapData.creditValues.avgSaving = 0;
    } else {
      nationalHeatMapData.creditValues.avgSaving =
        nationalHeatMapData.creditValues.avgSaving /
        totalStatesLengthForNationalCalc;
      nationalHeatMapData.creditValues.avgPrice =
        nationalHeatMapData.creditValues.avgPrice /
        totalStatesLengthForNationalCalc;
      nationalHeatMapData.prepayValues.avgSaving =
        nationalHeatMapData.prepayValues.avgSaving /
        totalStatesLengthForNationalCalc;
      nationalHeatMapData.prepayValues.avgPrice =
        nationalHeatMapData.prepayValues.avgPrice /
        totalStatesLengthForNationalCalc;
    }
    setNationalData(nationalHeatMapData);
  };
  useEffect(() => {
    if (settingsData !== null) {
      handleHeatMap();
      setSelectedColor(["#FFFF00", "#008000", "#FF0000"]);
    }
  }, [mutation, settingsData]);

  const handleStateReport = (value) => {
    setStateReport(value);
  };
  const handleSelectedState = (value) => {
    setStateArray(value);
  };
  const handleFilterDate = (value) => {
    setFilterData(value);
  };
  const handelModal = (value) => {
    setModal(value);
  };
  const handleSetMutation = () => {
    setMutation({});
  };

  useEffect(() => {
    if (settingsData !== null) {
      handleHeatMap();
    }
  }, [selectedNetwork, mutation, settingsData]);

  return (
    <>
      <Content>
        <Row>
          <Col sm={"12"}>
            <div className=" d-flex justify-content-md-end h-100 gap-1">
              <div className=" d-flex justify-content-md-end h-100 gap-1">
                <div className="form-group">
                  <label className="form-label">
                    Station Type
                  </label>
                  <ul className="custom-control-group g-3 align-center">
                    <li>
                      <div className="custom-control custom-radio custom-control-pro no-control">
                        <input
                          type="radio"
                          className="custom-control-input"
                          name="network"
                          value="loves"
                          id="stateBtnRadio2"
                          checked={selectedNetwork === "loves"}
                          onChange={(e) => setSelectedNetwork(e.target.value)}
                        />
                        <label className="custom-control-label" htmlFor="stateBtnRadio2">
                          Loves
                        </label>
                      </div>
                    </li>
                    <li>
                      <div className="custom-control custom-radio custom-control-pro no-control">
                        <input
                          type="radio"
                          className="custom-control-input"
                          name="network"
                          value="ta_petro"
                          id="stateBtnRadio1"
                          checked={selectedNetwork === "ta_petro"}
                          onChange={(e) => setSelectedNetwork(e.target.value)}
                        />
                        <label className="custom-control-label" htmlFor="stateBtnRadio1">
                          TA Petro
                        </label>
                      </div>
                    </li>
                  </ul>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    Filter Date
                  </label>
                  <CustomDatePicker
                    handleFilterDate={handleFilterDate}
                    setMutationCall={handleSetMutation}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">
                    &nbsp;
                  </label>
                  <div className="form-control-wrap w-md-auto">
                    <Button onClick={() => handelModal(true)} className="">
                      <span className=" px-1">State</span> <Icon name="filter" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </Col>
          <Col sm={"12"}>
            {errorMessage && (
              <div
                className="alert alert-danger d-block d-lg-none my-2"
                role="alert"
              >
                {errorMessage}
              </div>
            )}
          </Col>
        </Row>
        {mapLoading ? (
          <div
            className=" w-100 d-flex justify-content-center align-items-center "
            style={{ minHeight: "80vh" }}
          >
            <Spinner color="primary" />
          </div>
        ) : (
          <>
            <Row className="">
              <Col xs="12" sm="12" md="12" lg="8">
                <Row>
                  <Col className=" d-flex justify-content-center">
                    <div>
                      <FormGroup tag="fieldset" className="d-flex">
                        <FormGroup check className="d-flex gap-1">
                          <Label
                            style={{
                              backgroundColor: "#FF0000",
                              width: "50px",
                              height: "20px",
                            }}
                            htmlFor="FF0000"
                            className="mr-1 border"
                          ></Label>
                          <Input
                            type="checkbox"
                            name="colorFilter"
                            id="FF0000"
                            hidden
                            value="#FF0000"
                            checked={selectedColor.includes("#FF0000")}
                            onChange={() => handleColorChange("#FF0000")}
                          />
                          <Label
                            style={{
                              fontSize: "14px",
                              fontWeight: 500,
                              color: "#526484",
                            }}
                            htmlFor="FF0000"
                            className={
                              !selectedColor.includes("#FF0000")
                                ? "text-decoration-line-through-custom"
                                : ""
                            }
                          >
                            Red
                          </Label>
                        </FormGroup>

                        <FormGroup check className="d-flex gap-1">
                          <Label
                            style={{
                              backgroundColor: "#008000",
                              width: "50px",
                              height: "20px",
                            }}
                            htmlFor="008000"
                            className="mr-1 border"
                          ></Label>
                          <Input
                            type="checkbox"
                            hidden
                            name="colorFilter"
                            id="008000"
                            value="#008000"
                            checked={selectedColor.includes("#008000")}
                            onChange={() => handleColorChange("#008000")}
                          />
                          <Label
                            style={{
                              fontSize: "14px",
                              fontWeight: 500,
                              color: "#526484",
                            }}
                            htmlFor="008000"
                            className={
                              !selectedColor.includes("#008000")
                                ? "text-decoration-line-through-custom"
                                : ""
                            }
                          >
                            Green
                          </Label>
                        </FormGroup>

                        <FormGroup check className="d-flex gap-1">
                          <Label
                            style={{
                              backgroundColor: "#FFFF00",
                              width: "50px",
                              height: "20px",
                            }}
                            htmlFor="FFFF00"
                            className="mr-1 border"
                          ></Label>
                          <Input
                            type="checkbox"
                            hidden
                            name="colorFilter"
                            id="FFFF00"
                            value="#FFFF00"
                            checked={selectedColor.includes("#FFFF00")}
                            onChange={() => handleColorChange("#FFFF00")}
                          />
                          <Label
                            style={{
                              fontSize: "14px",
                              fontWeight: 500,
                              color: "#526484",
                            }}
                            htmlFor="FFFF00"
                            className={
                              !selectedColor.includes("#FFFF00")
                                ? "text-decoration-line-through-custom"
                                : ""
                            }
                          >
                            Yellow
                          </Label>
                        </FormGroup>
                      </FormGroup>
                    </div>
                  </Col>
                </Row>
                <Map
                  allStateDataGetOnce={stateData}
                  handleStateReport={handleStateReport}
                  stateData={stateFilterData}
                  mutation={mutation}
                  handleNavigate={handleNavigate}
                  localMutation={localMutation}
                  mapLoading={mapLoading}
                />
              </Col>

              <Col className="pt-5">
                {errorMessage && (
                  <div
                    className="alert alert-danger d-none d-lg-block"
                    role="alert"
                  >
                    {errorMessage}
                  </div>
                )}
                {!errorMessage && Object.keys(nationalData)?.length && (
                  <Block>
                    <Card className="mb-4 border">
                      <CardHeader
                        tag="h5"
                        className="bg-light text-dark text-center"
                      >
                        National Average Saving
                      </CardHeader>
                      <CardBody>
                        <Row className="g-4">
                          {/* Others Section */}
                          <Col xs={12}>
                            <Row className="g-2">
                              <Col>
                                <Card className="border">
                                  <CardHeader
                                    tag="h6"
                                    className="bg-light text-dark"
                                  >
                                    Prepay
                                  </CardHeader>
                                  <CardBody>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                      <div>Average Saving:</div>
                                      <div style={{ fontWeight: "bold" }}>
                                        {formatCurrency(
                                          nationalData?.prepayValues?.avgSaving
                                        )}
                                        /gallon
                                      </div>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                      <div>Average Price:</div>
                                      <div style={{ fontWeight: "bold" }}>
                                        {formatCurrency(
                                          nationalData?.prepayValues?.avgPrice
                                        )}
                                        /gallon
                                      </div>
                                    </div>
                                  </CardBody>
                                </Card>
                              </Col>
                              <Col>
                                <Card className="border">
                                  <CardHeader
                                    tag="h6"
                                    className="bg-light text-dark"
                                  >
                                    Credit
                                  </CardHeader>
                                  <CardBody>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                      <div>Average Saving:</div>
                                      <div style={{ fontWeight: "bold" }}>
                                        {formatCurrency(
                                          nationalData?.creditValues?.avgSaving
                                        )}
                                        /gallon
                                      </div>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                      <div>Average Price:</div>
                                      <div style={{ fontWeight: "bold" }}>
                                        {formatCurrency(
                                          nationalData?.creditValues?.avgPrice
                                        )}
                                        /gallon
                                      </div>
                                    </div>
                                  </CardBody>
                                </Card>
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>

                    <div ref={stateAverageSectionRef}>
                      {stateReport ? (
                        <Card className="mb-4 border">
                          <CardHeader
                            tag="h5"
                            className="bg-light text-dark text-center"
                          >
                            {stateReport?.state} Average Saving
                          </CardHeader>
                          <CardBody>
                            <Row className="g-4">
                              {/* Others Section */}
                              <Col xs={12}>
                                <Row className=" g-2">
                                  <Col>
                                    <Card className="border">
                                      <CardHeader
                                        tag="h6"
                                        className="bg-light text-dark"
                                      >
                                        Prepay
                                      </CardHeader>
                                      <CardBody>
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                          <div>Average Saving:</div>
                                          <div style={{ fontWeight: "bold" }}>
                                            {formatCurrency(
                                              stateReport?.prepaySaving
                                            )}
                                            /gallon
                                          </div>
                                        </div>
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                          <div>Average Price:</div>
                                          <div style={{ fontWeight: "bold" }}>
                                            {formatCurrency(
                                              stateReport?.prepayPrice
                                            )}
                                            /gallon
                                          </div>
                                        </div>
                                      </CardBody>
                                    </Card>
                                  </Col>
                                  <Col>
                                    <Card className="border">
                                      <CardHeader
                                        tag="h6"
                                        className="bg-light text-dark"
                                      >
                                        Credit
                                      </CardHeader>
                                      <CardBody>
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                          <div>Average Saving:</div>
                                          <div style={{ fontWeight: "bold" }}>
                                            {formatCurrency(
                                              stateReport?.creditSaving
                                            )}
                                            /gallon
                                          </div>
                                        </div>
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                          <div>Average Price:</div>
                                          <div style={{ fontWeight: "bold" }}>
                                            {formatCurrency(
                                              stateReport?.creditPrice
                                            )}
                                            /gallon
                                          </div>
                                        </div>
                                      </CardBody>
                                    </Card>
                                  </Col>
                                </Row>
                              </Col>
                            </Row>
                          </CardBody>
                        </Card>
                      ) : (
                        <div style={{ minHeight: "1px" }}></div> // Ensure the div is not empty initially
                      )}
                    </div>
                  </Block>
                )}
              </Col>
            </Row>
          </>
        )}
        <UsStateSelectModal
          selectedZone={selectedZone}
          setSelectedZone={handleSelectedZone}
          modalForm={modal}
          setModalForm={handelModal}
          userData={{
            setMutationCall: handleSetMutation,
            data: stateArray,
          }}
          handleSelectedState={handleSelectedState}
        />
      </Content>
    </>
  );
};

export default HeatMap;
